type SearchDataValue = {
  label: string
  value: string
  experience?: string
}

type SearchData = {
  country: SearchDataValue
  destination: SearchDataValue
  location: SearchDataValue
  budget: string[]
  duration: string[]
  experiences?: SearchDataValue[]
}

const getLabelFromString = (value: string) => {
  const result: string[] = []
  value.split(",").forEach((item) => result.push(item.split(":")[0]))
  return result.join()
}

const getIdFromString = (value: string) => {
  const result: string[] = []
  value?.split(",").forEach((item) => result.push(item?.split(":")[1]))
  const id = result.join()
  return id
}

// Helper function to get country name from country ID using available countries data
const getCountryNameById = (countryId: string, countries: any[] = []) => {
  // Try to find in the provided countries data (from Solr facets)
  const country = countries.find(c => c.value === countryId)
  return country?.label || countryId
}

// Helper function to get destination name from destination ID using available destinations data
const getDestinationNameById = (destinationId: string, destinations: any[] = []) => {
  // Try to find in the provided destinations data (from Solr facets)
  const destination = destinations.find(d => d.value === destinationId)
  return destination?.label || destinationId
}

type QueryRange = {
  min: number
  max: number
}

const getRangeFromString = (value: string = ""): QueryRange => {
  const parts = value.split("-")

  return {
    min: parseInt(parts?.[0]) || 0,
    max: parseInt(parts?.[1]) || 0,
  }
}

const formatSearchData = (data: SearchData) => {
  const country: string = data?.country?.value
  const destination: string = data?.destination?.value
  const tripStyles: string[] = []
  const tripTypes: string[] = []
  const duration: string[] = [data?.duration?.[0], data?.duration?.[1]]
  const budget: string[] = [data?.budget?.[0], data?.budget?.[1]]

  data?.experiences?.forEach((item: any) => {
    if (item?.experience === "Trip Styles") tripStyles.push(item?.value)
    else tripTypes.push(item?.value)
  })

  return {
    country,
    destination,
    tripStyles: tripStyles.join(","),
    tripTypes: tripTypes.join(","),
    duration: {
      min: duration[0],
      max: duration[1],
    },
    budget: {
      min: budget[0],
      max: budget[1],
    },
  }
}

const formatSearchDataByCountry = (data: SearchData) => {
  const location: string = data?.location?.value
  const country: string = data?.country?.value
  const tripStyles: string[] = []
  const tripTypes: string[] = []
  const duration: string[] = [data?.duration?.[0], data?.duration?.[1]]
  const budget: string[] = [data?.budget?.[0], data?.budget?.[1]]

  data?.experiences?.forEach((item: any) => {
    if (item?.experience === "Trip Styles") tripStyles.push(item?.value)
    else tripTypes.push(item?.value)
  })

  return {
    location,
    country,
    tripStyles: tripStyles.join(","),
    tripTypes: tripTypes.join(","),
    duration: {
      min: duration[0],
      max: duration[1],
    },
    budget: {
      min: budget[0],
      max: budget[1],
    },
  }
}

const setFormFields = (params: any, countries: any[] = [], destinations: any[] = []) => {
  const result: any = {}
  const experiences: any = []
  if (params?.location) {
    result.location = {
      label: getLabelFromString(params?.location),
      value: getIdFromString(params?.location),
    }
  }

  if (params?.country) {
    // Handle country ID format - look up name from countries data
    const countryId = params?.country
    const countryName = getCountryNameById(countryId, countries)
    result.country = {
      label: countryName,
      value: countryId,
    }
  }

  if (params?.destination) {
    // Handle destination ID format - look up name from destinations data
    const destinationId = params?.destination
    const destinationName = getDestinationNameById(destinationId, destinations)
    result.destination = {
      label: destinationName,
      value: destinationId,
    }
  }

  if (params?.tripStyles) {
    const array = params?.tripStyles.split(",")
    array.forEach((item: string) => {
      experiences.push({ label: getLabelFromString(item), experience: "Trip Styles" })
    })
  }

  if (params?.tripTypes) {
    const array = params?.tripTypes.split(",")
    array.forEach((item: string) => {
      experiences.push({ label: getLabelFromString(item), experience: "Trip Types" })
    })
  }

  if (params?.duration) {
    const range = getRangeFromString(params?.duration)
    result.duration = {
      label: getLabelFromString(params?.duration),
      value: [range.min, range.max],
    }
  }

  if (params?.budget) {
    const range = getRangeFromString(params?.budget)
    result.budget = {
      label: getLabelFromString(params?.budget),
      value: [range.min, range.max],
    }
  }

  return {
    ...result,
    experiences,
  }
}

const getSortOption = (param: string) => {
  if (param) {
    const sortLabel = param.split("-")[0]
    const sortType = param.split("-")[1]
    const isPriceSort = sortLabel.includes("price")
    return isPriceSort ? `price-${sortType}` : `its_duration-${sortType}`
  }
  return null
}

export {
  getLabelFromString,
  getIdFromString,
  getRangeFromString,
  formatSearchData,
  formatSearchDataByCountry,
  setFormFields,
  getSortOption,
  getCountryNameById,
  getDestinationNameById,
}
